import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft,
  Search,
  Layers,
  Info,
  X,
  List,
  MapPin,
  PersonStanding,
  Bike,
  Clock,
  ArrowRight,
  Zap,
  Route,
  Download,
  Trash2,
  EyeOff,
  Eye,
  Filter,
  User,
  BadgePercent,
  Wand,
  Ticket,
  Compass,
  ChevronDown,
  ChevronUp,
  Flag,
  Home,
  Plus,
  Minus,
  Tag,
  Star,
  XCircle,
  Globe,
  Instagram,
  Facebook
} from 'lucide-react';
import { Link } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import TurnByTurnNavigation from './TurnByTurnNavigation';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { QRCodeCanvas } from 'qrcode.react';

import {
  getLocalizedPoiName,
  getLocalizedPoiDescription,
  getLocalizedPoiType
} from '../../utils/localizationHelpers';

import { extractInstagramHandle } from '../../utils/socialHelpers';

import config from '../../config';

// Get user id (or null)
import { getUserId } from '../../lib/auth';

// Get components
import OffersList from '../../components/OffersList';
import ExperiencesList from '../../components/ExperiencesList';
import BikeRentalList from '../../components/BikeRentalList';
import UserProfile from '../../components/UserProfile';
import MobilePoiDrawer from '../../components/MobilePoiDrawer';

// Import brand (whitelabeling)
import brand from '../../whitelabel';

// Set your access token
mapboxgl.accessToken = 'pk.eyJ1IjoibTJvdGVjaCIsImEiOiJjbTczbzU4aWQwMWdmMmpzY3N4ejJ3czlnIn0.fLDR4uG8kD8-g_IDM8ZPdQ';

const IntegratedMap = ({ initialRouteId, initialPoiId, initialMode, currentLanguage }) => {
  
  const [user, setUser] = useState(null);
  const token = localStorage.getItem('token');
  
  const mapContainer = useRef(null);
  const [map, setMap] = useState(null);
  
  // Add useTranslation hook
  const { t } = useTranslation();
  
  // Initialize language state
  const [language, setLanguage] = useState(currentLanguage || 'en');

  // Update language when prop changes
  useEffect(() => {
    if (currentLanguage) {
      setLanguage(currentLanguage);
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const res = await fetch(config.getApiUrl('users/me'), {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          if (res.ok && data.user) {
            setUser(data.user);
          } else {
            setUser(null);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          setUser(null);
        }
      }
    };

    fetchUser();
  }, [token]);
  
  // Watch for language changes in localStorage
  useEffect(() => {
    const handleLanguageChange = () => {
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedLanguage && savedLanguage !== language) {
        setLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
    };
    
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, [language]);
  
  // Sidebar control
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Default to closed on mobile
  const [activeSidebarTab, setActiveSidebarTab] = useState(initialMode || 'pois'); // 'pois', 'routes', 'planner'
  
  // Mobile view state
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isGpxLoading, setIsGpxLoading] = useState(false);
  const [isRoutingLoading, setIsRoutingLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // POI data and filters
  const [pois, setPois] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredPlace, setHoveredPlace] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [poiCategoryFilter, setPoiCategoryFilter] = useState('all');
  const [visiblePois, setVisiblePois] = useState(new Set());
  
  // GPX route data and filters
  const [bikeRoutes, setBikeRoutes] = useState([]);
  const [bikeRouteSearchQuery, setBikeRouteSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [roadTypeFilter, setRoadTypeFilter] = useState('all');
  const [selectedBikeRoute, setSelectedBikeRoute] = useState(null);
  const [visibleRoutes, setVisibleRoutes] = useState(new Set());

  // Offers
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerSearchQuery, setOfferSearchQuery] = useState('');

  // Offers
  const [experiences, setExperiences] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [experienceSearchQuery, setExperienceSearchQuery] = useState('');

  // Route planning
  const [isRoutingPanelOpen, setIsRoutingPanelOpen] = useState(false);
  const [routeMode, setRouteMode] = useState('walking'); // 'walking' or 'cycling'
  const [routePreference, setRoutePreference] = useState('shortest'); // 'shortest' or 'fastest'
  const [waypoints, setWaypoints] = useState([]);
  const [currentWaypointIndex, setCurrentWaypointIndex] = useState(0);
  const [routeSelectionStep, setRouteSelectionStep] = useState(0); // 0: none, 1: selecting waypoints, 2: route calculated
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeAlternatives, setRouteAlternatives] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [mapClickEnabled, setMapClickEnabled] = useState(false);
  
  // Map layers visibility control
  const [showPois, setShowPois] = useState(true);
  const [showBikeRoutes, setShowBikeRoutes] = useState(true);
  const [mapListView, setMapListView] = useState('map'); // 'map' or 'list'

  // Show button to clear routes
  const [showClearRouteButton, setShowClearRouteButton] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const hideTimeoutRef = useRef(null);

  // NEW: POI-to-POI navigation state
  const [poiNavigationMode, setPoiNavigationMode] = useState('none'); // 'none', 'from', 'to'
  const [startingPoi, setStartingPoi] = useState(null);
  const startingPoiRef = useRef(null);

  // Initialize navigation state
  useEffect(() => {
    // Initialize or reset navigation state when component mounts
    setPoiNavigationMode('none');
    setStartingPoi(null);
  }, []);

  // Show/hide buttons according to app inactivity
  useEffect(() => {
    if (!map) return;

    const handleActivity = () => {
      if (showClearRouteButton) {
        setButtonVisible(true);
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
          setButtonVisible(false);
        }, 4000); // 4 seconds of inactivity
      }
    };

    map.on('dragstart', handleActivity);
    map.on('zoomstart', handleActivity);
    map.on('click', handleActivity);

    return () => {
      map.off('dragstart', handleActivity);
      map.off('zoomstart', handleActivity);
      map.off('click', handleActivity);
      clearTimeout(hideTimeoutRef.current);
    };
  }, [map, showClearRouteButton]);

  const poiCategoryColors = {
    //'business': '#3b82f6',     // Blue
    'business': '#31d33a',     // SAK Green
    'cultural': '#8B4513',     // Brown
    'landscape': '#1e5631',    // Green
    'religious': '#8B5CF6',    // Purple
    'landscape_religious': '#1E5631', // Dark Green
    'default': '#22c55e'       // Default Green
  };
  
  // Check if in mobile view
  useEffect(() => {
    const checkMobileView = () => {
      //const isMobile = window.innerWidth < 768;
      // FORCE MOBILE VIEW (temporary)
      const isMobile = true;
      setIsMobileView(isMobile);
      console.log('Mobile view? ' + isMobile);
      
      // Always show POIs in mobile view
      setShowPois(isMobile);
    };
    
    // Initial check
    checkMobileView();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobileView);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);
  
  // Collection of refs for cleanup
  const markersRef = useRef([]);
  const poiMarkersRef = useRef({});
  const waypointMarkersRef = useRef([]);
  const hoverPopupRef = useRef(null);
  const routeLayerIds = useRef(new Set());
  const routeSourceIds = useRef(new Set());
  
  // IMPROVEMENT 3: Track route markers by route ID for better show/hide control
  const routeMarkersRef = useRef({}); // { routeId: [startMarker, endMarker] }
  
  // IMPROVEMENT 1: Enhanced color maps with better contrast
  const routeColors = {
    'easy': '#22c55e',      // Green
    'medium': '#f59e0b',    // Amber/Orange
    'hard': '#ef4444',      // Red
    'default': '#3b82f6'    // Blue
  };
  
  const routeModeColors = {
    'walking': '#22c55e',   // Green
    'cycling': '#3b82f6'    // Blue
  };
  
  // Track click time for double-click detection
  const lastClickTime = useRef(0);
  const clickTimeout = useRef(null);

 useEffect(() => {
  if (!mapContainer.current) return;
  
  // [lng, lat] for Mapbox GL
  const defaultCenter = brand.mapCenter || [11.907, 42.685]; // fallback to San Lorenzo Nuovo
  
  try {
    // Create map instance
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v11',
      center: defaultCenter,
      zoom: 13
    });
    
    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    
    mapInstance.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserLocation: true,
        showAccuracyCircle: true
      }),
      'bottom-right'
    );
    
    // Set map when loaded
    mapInstance.on('load', () => {
      console.log('Map loaded successfully');
      setMap(mapInstance);
    });
    
    return () => {
      // Clean up map on unmount
      if (mapInstance) {
        // Clean up markers
        markersRef.current.forEach(marker => {
          if (marker) marker.remove();
        });
        
        Object.values(poiMarkersRef.current).forEach(marker => {
          if (marker) marker.remove();
        });
        
        waypointMarkersRef.current.forEach(marker => {
          if (marker) marker.remove();
        });

        // IMPROVEMENT 3: Clean up route markers
        Object.values(routeMarkersRef.current).forEach(markers => {
          if (markers && Array.isArray(markers)) {
            markers.forEach(marker => {
              if (marker) marker.remove();
            });
          }
        });
        
        if (hoverPopupRef.current) {
          hoverPopupRef.current.remove();
        }
        
        // Clean up layers and sources
        routeLayerIds.current.forEach(id => {
          if (mapInstance.getLayer(id)) {
            mapInstance.removeLayer(id);
          }
        });
        
        routeSourceIds.current.forEach(id => {
          if (mapInstance.getSource(id)) {
            mapInstance.removeSource(id);
          }
        });
        
        // Clean up routing data
        if (mapInstance.getLayer('route')) {
          mapInstance.removeLayer('route');
        }
        if (mapInstance.getSource('route')) {
          mapInstance.removeSource('route');
        }
        
        // Remove map instance
        mapInstance.remove();
      }
    };
  } catch (error) {
    console.error('Error initializing map:', error);
    setError(`Failed to initialize map: ${error.message}`);
  }
}, []);
  
  // Fetch POIs from API
  useEffect(() => {
    const fetchPois = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(config.getApiUrl('pois'));
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch POIs');
        }
        
        setPois(data.data || []);
        console.log('POIs loaded:', data.data?.length || 0);
      } catch (err) {
        console.error('Error fetching POIs:', err);
        setError(`Failed to load POIs: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPois();
  }, []);
  
  // Fetch bike routes from API
  useEffect(() => {
    const fetchBikeRoutes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(config.getApiUrl('bike-routes'));
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch bike routes');
        }
        
        setBikeRoutes(data.data || []);
        console.log('Bike routes loaded:', data.data?.length || 0);
      } catch (err) {
        console.error('Error fetching bike routes:', err);
        setError(`Failed to load bike routes: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBikeRoutes();
  }, []);
  
  // Handle initial route or POI loading
  useEffect(() => {
    if (!map || !bikeRoutes.length || !pois.length) return;
    
    if (initialRouteId) {
      const route = bikeRoutes.find(r => r._id === initialRouteId);
      if (route) {
        fetchAndDisplayGpx(initialRouteId);
      }
    }
    
    if (initialPoiId) {
      const poi = pois.find(p => p._id === initialPoiId);
      if (poi) {
        setSelectedPlace(poi);
        const coords = getMapboxCoords(poi);
        if (coords) {
          map.flyTo({
            center: coords,
            zoom: 25,
            essential: true
          });
        }
      }
    }
    
    // Set initial mode if provided
    if (initialMode) {
      setActiveSidebarTab(initialMode);
    }
  }, [map, bikeRoutes, pois, initialRouteId, initialPoiId, initialMode]);

  // Fetch Offers from API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(config.getApiUrl('offers'));
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch offers');
        }
        
        setOffers(data.data || []);
        console.log('Offers loaded:', data.data?.length || 0);
      } catch (err) {
        console.error('Error fetching offers:', err);
        setError(`Failed to load offers: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffers();
  }, []);  
  
  // Add POI markers to map when map is ready and POIs are loaded
  useEffect(() => {
    if (!map || !pois.length) return;
    
    console.log('Adding POI markers to map');
    
    // Clear existing POI markers
    Object.values(poiMarkersRef.current).forEach(marker => {
      if (marker) marker.remove();
    });
    poiMarkersRef.current = {};
    
    // Add markers for each POI
    pois.forEach(poi => {
      addPoiMarker(poi);
    });
    
    // Initially set all POIs as visible
    setVisiblePois(new Set(pois.map(poi => poi._id)));
    
  }, [map, pois, language]); // Added language dependency for marker refresh when language changes
  
  // Helper to convert POI coordinates to Mapbox format
  const getMapboxCoords = (poi) => {
    if (!poi || (!poi.coordinates && typeof poi.coordinates !== 'string')) return null;
    
    let coords;
    if (typeof poi.coordinates === 'string') {
      const coordsArray = poi.coordinates.split(',').map(coord => parseFloat(coord.trim()));
      if (coordsArray.length === 2 && !isNaN(coordsArray[0]) && !isNaN(coordsArray[1])) {
        coords = [coordsArray[1], coordsArray[0]]; // [lng, lat] for Mapbox
      } else {
        console.warn('Invalid string coordinates format', poi.coordinates);
        return null;
      }
    } else if (poi.coordinates?.lat && poi.coordinates?.lng) {
      if (isNaN(poi.coordinates.lat) || isNaN(poi.coordinates.lng)) {
        console.warn('Invalid object coordinates values', poi.coordinates);
        return null;
      }
      coords = [poi.coordinates.lng, poi.coordinates.lat];
    } else {
      return null;
    }
    
    return coords;
  };
  
  // Add a POI marker to the map
const addPoiMarker = (poi) => {
  if (!map) return null;
  
  // Parse coordinates
  const coords = getMapboxCoords(poi);
  if (!coords) {
    console.warn(`Invalid coordinates for POI: ${getLocalizedPoiName(poi)}`);
    return null;
  }
  
  // Determine marker color based on category
  const poiColor = poiCategoryColors[poi.category] || poiCategoryColors.default;
  
  // Create custom HTML element for marker
  const el = document.createElement('div');
  el.className = 'poi-marker marker';
  el.style.backgroundImage = `url('/pointer.png')`;
  el.style.width = '77px';
  el.style.height = '77px';
  el.style.backgroundSize = 'contain';
  el.style.backgroundRepeat = 'no-repeat';
  el.style.cursor = 'pointer';
  
  // Add category class for filtering and color
  el.classList.add(`category-${poi.category || 'default'}`);
  
  // Apply color using a CSS variable or a color overlay
  el.style.filter = `hue-rotate(${getHueRotation(poiColor)})`;
  
  // CREATE MOBILE-OPTIMIZED SHORT POPUP
  const createShortPopup = () => {
    const shortPopupContent = `
      <div class="mobile-short-popup bg-white rounded-lg shadow-lg p-3 max-w-xs">
        <div class="flex items-start">
          <div class="w-12 h-12 flex-shrink-0 bg-gray-100 rounded overflow-hidden mr-3">
            ${poi.photo ? 
              `<img src="${poi.photo}" alt="${getLocalizedPoiName(poi)}" class="w-full h-full object-cover">` : 
              `<div class="w-full h-full flex items-center justify-center bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${poiColor}" stroke-width="2">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>`
            }
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="font-semibold text-sm text-gray-900 mb-1">${getLocalizedPoiName(poi)}</h3>
            <p class="text-xs text-gray-600 mb-2" style="color: ${poiColor}">
              ${poi.category 
                ? t('map.poi.categories.' + poi.category, poi.category.replace('_', ' '))
                : getLocalizedPoiType(poi)
              }
            </p>
            ${getLocalizedPoiDescription(poi) ? 
              `<p class="text-xs text-gray-500 mb-2 line-clamp-2">${getLocalizedPoiDescription(poi).substring(0, 80)}...</p>` : 
              ''
            }
            <div class="flex flex-wrap gap-1">
              <button class="mobile-popup-more text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600">
                ${t('map.more', 'More')}
              </button>
              <button class="mobile-popup-navigate text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600">
                ${t('map.planner.navigateFromLocation', 'Arrive here')}
              </button>
              <button class="mobile-popup-route text-xs bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600">
                ${t('map.planner.navigateFromThisPoi', 'Start from here')}
              </button>
            </div>
          </div>
          <button class="mobile-popup-close flex-shrink-0 p-1 text-gray-400 hover:text-gray-600">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    `;

    return new mapboxgl.Popup({ 
      offset: 25,
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      className: 'mobile-poi-popup',
      focusAfterOpen: false,
      maxWidth: '320px'
    }).setHTML(shortPopupContent);
  };

  // Create marker with specific options to keep it fixed
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'map',
    rotationAlignment: 'map',
    offset: [0, 0]
  })
  .setLngLat(coords)
  .addTo(map);
  
  // Handle marker click - MOBILE OPTIMIZED
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    
    if (isMobileView) {
      // Center the map on the POI
      const coords = getMapboxCoords(poi);
      if (coords && map) {
        map.flyTo({
          center: coords,
          zoom: 20,
          essential: true
        });
      }

      // Close any existing popups
      if (window.currentMobilePopup) {
        window.currentMobilePopup.remove();
        window.currentMobilePopup = null;
      }
      
      // Create and show short popup
      const shortPopup = createShortPopup();
      shortPopup.setLngLat(coords).addTo(map);
      window.currentMobilePopup = shortPopup;
      
      // Add event listeners after popup is added to DOM
      setTimeout(() => {
        const popupElement = document.querySelector('.mobile-poi-popup');

        if (popupElement) {
          // More button - opens full sidebar
          const moreBtn = popupElement.querySelector('.mobile-popup-more');
          if (moreBtn) {
            moreBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              setSelectedPlace(poi);
              //setActiveSidebarTab('pois');
              //setMapListView('list');
              //setIsSidebarOpen(true);
              shortPopup.remove();
              window.currentMobilePopup = null;
            });
          }
          
          // Navigate button - navigate from current location
          const navigateBtn = popupElement.querySelector('.mobile-popup-navigate');
          if (navigateBtn) {
            navigateBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              navigateToPoi(poi);
              shortPopup.remove();
              window.currentMobilePopup = null;
            });
          }
          
          // Route button - start route planning
          const routeBtn = popupElement.querySelector('.mobile-popup-route');
          if (routeBtn) {
            routeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              navigateFromPoi(poi);
              shortPopup.remove();
              window.currentMobilePopup = null;
            });
          }
          
          // Close button
          const closeBtn = popupElement.querySelector('.mobile-popup-close');
          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              shortPopup.remove();
              window.currentMobilePopup = null;
            });
          }
        }
      }, 100);
      
    } else {
      // Desktop behavior - existing logic
      if (isRoutingPanelOpen) {
        selectPoiAsWaypoint(poi);
      } else if (poiNavigationMode === 'from') {
        navigateToPoi(poi);
      } else {
        setSelectedPlace(poi);
        setActiveSidebarTab('pois');
        setIsSidebarOpen(true);
      }
    }
  });
  
  // Store the marker reference
  poiMarkersRef.current[poi._id] = marker;

  return marker;
};

const getHueRotation = (targetColor) => {
  switch (targetColor.toLowerCase()) {
    case '#3b82f6': // Blue
      return '120deg';
    case '#8b4513': // Brown
      return '-90deg';
    case '#22c55e': // Green
      return '0deg';
    case '#31d33a': // SAK Green (Business)
      return '0deg'; // Already green, no rotation needed
    case '#8b5cf6': // Purple
      return '150deg';
    case '#1e5631': // Dark Green
      return '-20deg';
    case '#14532d': // Extra Dark Green
      return '-35deg';
    default:
      return '0deg';
  }
};
  
  
  // Update POI markers visibility based on filters
  useEffect(() => {
    if (!map) return;

    console.log('Filtering POIs');
    
    console.log("Show POIs: " + showPois);

    const filteredPois = filterPois();
    
    console.log('Search Query:', searchQuery);
    console.log('Filtered POIs:', filteredPois.length);

    // Get set of filtered POI IDs
    const filteredPoiIds = new Set(filteredPois.map(poi => poi._id));
    
    // Update markers visibility
    Object.entries(poiMarkersRef.current).forEach(([poiId, marker]) => {
      if (!marker) return;
      
      const shouldBeVisible = filteredPoiIds.has(poiId) && showPois;

      console.log("POI should be visible?" + shouldBeVisible);
      
      // Get marker element
      const el = marker.getElement();
      
      if (shouldBeVisible) {
        el.style.display = 'block';
      } else {
        el.style.display = 'none';
      }
    });
    
  }, [map, searchQuery, poiCategoryFilter, showPois]);

// Map click handler to close mobile popups
useEffect(() => {
  if (map && isMobileView) {
    const handleMapClick = (e) => {
      // Check if click is on map (not on marker or popup)
      const isMarkerClick = e.originalEvent.target.closest('.marker') || 
                           e.originalEvent.target.closest('.mapboxgl-popup');
      
      if (!isMarkerClick && window.currentMobilePopup) {
        window.currentMobilePopup.remove();
        window.currentMobilePopup = null;
      }
    };
    
    map.on('click', handleMapClick);
    
    return () => {
      map.off('click', handleMapClick);
    };
  }
}, [map, isMobileView]);
  
// Filter POIs based on search and category
const filterPois = () => {
  //return pois;

  if (!pois.length) return [];
  
  return pois.filter(poi => {
    // Apply search filter
    const matchesSearch = !searchQuery || 
      getLocalizedPoiName(poi)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getLocalizedPoiType(poi)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getLocalizedPoiDescription(poi)?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply category filter
    const matchesCategory = poiCategoryFilter === 'all' || 
      poi.category === poiCategoryFilter;
    
    return matchesSearch && matchesCategory;
  });
};

const lang = i18n.language || 'en';

// Filter bike routes based on search and filters
const filterBikeRoutes = () => {
  if (!bikeRoutes.length) return [];
  
  return bikeRoutes.filter(route => {
    // Apply search filter
    const matchesSearch = !bikeRouteSearchQuery || 
      route.name?.[lang]?.toLowerCase().includes(bikeRouteSearchQuery.toLowerCase()) ||
      route.description?.[lang]?.toLowerCase().includes(bikeRouteSearchQuery.toLowerCase()) ||
      route.roadType?.toLowerCase().includes(bikeRouteSearchQuery.toLowerCase());
    
    // Apply difficulty filter
    const matchesDifficulty = difficultyFilter === 'all' || 
      route.difficulty === difficultyFilter;
    
    // Apply road type filter
    const matchesRoadType = roadTypeFilter === 'all' || 
      route.roadType === roadTypeFilter;
    
    return matchesSearch && matchesDifficulty && matchesRoadType;
  });
};

// Filter offers based on search and filters
const filterOffers = () => {
  if (!offers.length) return [];

  return offers.filter((offer) => {
    const search = offerSearchQuery.toLowerCase();
    return (
      !offerSearchQuery ||
      offer.name?.[language]?.toLowerCase().includes(search) ||
      offer.description?.[language]?.toLowerCase().includes(search)
    );
  });
};

// NEW: POI-to-POI navigation function
// To be called when clicking "Go Here"
const navigateToPoi = (destinationPoi) => {
  // Close any mobile popup
  if (window.currentMobilePopup) {
    window.currentMobilePopup.remove();
    window.currentMobilePopup = null;
  }

  if (startingPoiRef.current) {
    const fromPoi = startingPoiRef.current;
    startingPoiRef.current = null;
    setStartingPoi(null);
    setPoiNavigationMode('none');

    setActiveSidebarTab('planner');
    setIsRoutingPanelOpen(true);
    clearRoutePlanning();
    setWaypoints([fromPoi, destinationPoi]);
    setRouteSelectionStep(2);
    setError(null);
    fetchMultiPointRoute([fromPoi, destinationPoi], routeMode, routePreference);

    if (isMobileView) {
      setMapListView('map');
      setIsSidebarOpen(false);
      //setError(t('map.routeCalculated', 'Route calculated between POIs!'));
      setTimeout(() => setError(null), 4000);
    }
  } else {
    // No starting point — default to current location
    navigateFromCurrentLocation(destinationPoi);
  }
};

const navigateFromPoi = (poi) => {
  startingPoiRef.current = poi;
  setStartingPoi(poi);
  setPoiNavigationMode('from');
  setSelectedPlace(null);

  if (isMobileView) {
    setTimeout(() => {
      if (poiNavigationMode === 'from') {
        setError(t('map.tapAnotherPoi', 'Tap another POI to complete the route'));
      }
    }, 3000);
  } else {
    setError(t('map.selectDestinationPoi', 'Starting point set. Now select a destination POI'));
  }
};

const navigateToPoiOLD = (destinationPoi) => {
  // Close popup
  if (window.currentMobilePopup) {
    window.currentMobilePopup.remove();
    window.currentMobilePopup = null;
  }

  if (startingPoiRef.current) {
    const fromPoi = startingPoiRef.current;

    // Clear everything
    setStartingPoi(null);
    startingPoiRef.current = null;
    setPoiNavigationMode('none');

    // Plan route
    setActiveSidebarTab('planner');
    setIsRoutingPanelOpen(true);
    clearRoutePlanning();
    setWaypoints([fromPoi, destinationPoi]);
    setRouteSelectionStep(2);
    setError(null);
    fetchMultiPointRoute([fromPoi, destinationPoi], routeMode, routePreference);

    if (isMobileView) {
      setMapListView('map');
      setIsSidebarOpen(false);
      //setError(t('map.routeCalculated', 'Route calculated between POIs!'));
      //setTimeout(() => setError(null), 4000);
    }
  } else {
    // First tap
    startingPoiRef.current = destinationPoi;
    setStartingPoi(destinationPoi);
    setPoiNavigationMode('from');

    if (isMobileView) {
      setTimeout(() => {
        if (poiNavigationMode === 'from') {
          setError(t('map.tapAnotherPoi', 'Tap another POI to complete the route'));
        }
      }, 3000);
    } else {
      setError(t('map.selectDestinationPoi', "Starting point set. Now select a destination POI"));
    }

    setSelectedPlace(null);
  }
};

// Add function to cancel navigation mode
const cancelPoiNavigation = () => {
  setStartingPoi(null);
  setPoiNavigationMode('none');
  setError(null);
};

// Function to fetch and display a GPX track
const fetchAndDisplayGpx = async (routeId) => {
  if (!map || !routeId) return;
  
  try {
    setIsGpxLoading(true);
    setError(null);
    
    // Fetch route details first
    const routeResponse = await fetch(config.getApiUrl(`bike-routes/${routeId}`));
    
    if (!routeResponse.ok) {
      throw new Error(`Failed to fetch route details: ${routeResponse.status}`);
    }
    
    const routeData = await routeResponse.json();
    
    if (!routeData.success) {
      throw new Error(routeData.message || 'Failed to fetch route details');
    }
    
    const route = routeData.data;
    
    // Check if this route is already loaded (path data exists)
    if (route.path && route.path.length > 0) {
      displayRouteFromPath(route);
    } else {
      // Fetch the route path data
      const pathResponse = await fetch(config.getApiUrl(`bike-routes/${routeId}/path`));
      
      if (!pathResponse.ok) {
        throw new Error(`Failed to fetch route path: ${pathResponse.status}`);
      }
      
      const pathData = await pathResponse.json();
      
      if (!pathData.success) {
        throw new Error(pathData.message || 'Failed to fetch route path');
      }
      
      // Display the route with the retrieved path data
      displayRouteFromGeoJson(route, pathData.data);
    }
    
    // Add route to visible routes set
    setVisibleRoutes(prev => new Set(prev).add(routeId));
    
    // Update selected route
    setSelectedBikeRoute(route);
    
    // Switch to routes tab
    setActiveSidebarTab('routes');
    setIsSidebarOpen(true);
    
  } catch (err) {
    console.error('Error fetching GPX data:', err);
    setError(`Failed to load route: ${err.message}`);
  } finally {
    setIsGpxLoading(false);
  }
};

// Display route directly from path data
const displayRouteFromPath = (route) => {
  if (!map || !route.path || route.path.length === 0 || !showBikeRoutes) return;
  
  const lang = i18n.language || 'en';

  // Create a GeoJSON object from the path
  const geojson = {
    type: 'Feature',
    properties: {
      name: route.name?.[lang] || route.name?.en || '',
      difficulty: route.difficulty,
      roadType: route.roadType,
      id: route._id
    },
    geometry: {
      type: 'LineString',
      coordinates: route.path
    }
  };
  
  // Add to map
  addRouteToMap(route._id, geojson, route.difficulty);
  
  // Add start and end markers
  addRouteMarkers(route);
  
  // Fit to route bounds if it's the only visible route
  if (visibleRoutes.size === 0) {
    fitMapToRoute(route.path);
  }
};

// Display route from GeoJSON data
const displayRouteFromGeoJson = (route, geoJson) => {
  if (!map || !geoJson || !geoJson.geometry || !geoJson.geometry.coordinates || !showBikeRoutes) return;
  
  // Add to map
  addRouteToMap(route._id, geoJson, route.difficulty);
  
  // Add start and end markers
  addRouteMarkers(route, geoJson.geometry.coordinates);
  
  // Fit to route bounds if it's the only visible route
  if (visibleRoutes.size === 0) {
    fitMapToRoute(geoJson.geometry.coordinates);
  }
};
// ALTERNATIVE: Simpler addRouteToMap function without feature state hover effects
const addRouteToMap = (routeId, geoJson, difficulty = 'default') => {
  const sourceId = `route-source-${routeId}`;
  const layerId = `route-layer-${routeId}`;
  
  // IMPROVEMENT 1: Get color based on difficulty with fallback
  const routeColor = routeColors[difficulty] || routeColors[geoJson.properties?.difficulty] || routeColors.default;
  console.log(`Route ${routeId} difficulty: ${difficulty}, color: ${routeColor}`);
  
  // Add source if it doesn't exist
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'geojson',
      data: geoJson
    });
    
    // Track the source for cleanup
    routeSourceIds.current.add(sourceId);
  } else {
    // Update the source if it exists
    map.getSource(sourceId).setData(geoJson);
  }
  
  // Add layer if it doesn't exist
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        'visibility': 'visible'
      },
      paint: {
        'line-color': routeColor, // IMPROVEMENT 1: Use calculated color
        'line-width': 4,
        'line-opacity': 0.8
      }
    });
    
    // Track the layer for cleanup
    routeLayerIds.current.add(layerId);
    
    // Simple hover effect without feature state
    map.on('mouseenter', layerId, () => {
      map.getCanvas().style.cursor = 'pointer';
      // Increase line width on hover using setPaintProperty
      map.setPaintProperty(layerId, 'line-width', 6);
    });
    
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = '';
      // Reset line width when not hovering
      map.setPaintProperty(layerId, 'line-width', 4);
    });
    
    // Add click handler
    map.on('click', layerId, (e) => {
      // Use the routeId directly since we know which route this layer represents
      const clickedRoute = bikeRoutes.find(r => r._id === routeId);
      if (clickedRoute) {
        setSelectedBikeRoute(clickedRoute);
        setActiveSidebarTab('routes');
        setIsSidebarOpen(true);
        
        // On mobile, switch to list view to show route details
        if (isMobileView) {
          setMapListView('list');
        }
      }
    });
  }
};

// IMPROVEMENT 3: Enhanced addRouteMarkers function with better tracking
const addRouteMarkers = (route, coordinates = null) => {
  // Use coordinates from parameters or from route
  const routeCoords = coordinates || route.path;
  if (!routeCoords || routeCoords.length === 0) return;
  
  // Get start and end coordinates
  const startCoords = routeCoords[0];
  const endCoords = routeCoords[routeCoords.length - 1];
  
  // Get route color for markers
  const routeColor = routeColors[route.difficulty] || routeColors.default;
  
  // Create markers
  const createMarker = (coords, isStart) => {
    // Create custom HTML element for marker
    const el = document.createElement('div');
    el.className = 'route-marker marker';
    el.style.width = '28px';
    el.style.height = '28px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = isStart ? routeColor : '#ef4444'; // Start gets route color, end gets red
    el.style.border = '3px solid white';
    el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.color = 'white';
    el.style.fontWeight = 'bold';
    el.style.fontSize = '12px';
    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.innerHTML = isStart ? 'S' : 'E';
    
    // Create popup
    const lang = i18n.language || 'en';
    const localizedName = route.name?.[lang] || route.name?.en || '';

    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: false
    })
    .setHTML(`
      <div class="popup-content">
        <h3 class="font-bold">${localizedName}</h3>
        <p class="text-sm" style="color: ${routeColor}">
          ${t(`map.difficulty.${route.difficulty}`, route.difficulty || 'Unknown')} • 
          ${isStart ? t('map.poi.details.starting', "Starting Point") : t('map.poi.details.destination', "End Point")}
        </p>
      </div>
    `);
    
    // Create marker with improved settings
    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
      pitchAlignment: 'map',
      rotationAlignment: 'map',
      offset: [0, 0]
    })
      .setLngLat(coords)
      .setPopup(popup)
      .addTo(map);
    
    return marker;
  };
  
  // Add markers
  const startMarker = createMarker(startCoords, true);
  const endMarker = createMarker(endCoords, false);
  
  // IMPROVEMENT 3: Store markers by route ID for better tracking
  if (!routeMarkersRef.current[route._id]) {
    routeMarkersRef.current[route._id] = [];
  }
  routeMarkersRef.current[route._id] = [startMarker, endMarker];
  
  // Also store in general markers ref for cleanup
  markersRef.current.push(startMarker, endMarker);
};

// IMPROVEMENT 2 & 3: Enhanced route visibility update with marker control
useEffect(() => {
  if (!map) return;
  
  const filteredRoutes = filterBikeRoutes();
  
  // Get set of filtered route IDs
  const filteredRouteIds = new Set(filteredRoutes.map(route => route._id));
  
  // Update route layers visibility
  routeLayerIds.current.forEach(layerId => {
    const routeId = layerId.replace('route-layer-', '');
    
    if (map.getLayer(layerId)) {
      const shouldBeVisible = filteredRouteIds.has(routeId) && showBikeRoutes;
      map.setLayoutProperty(
        layerId, 
        'visibility', 
        shouldBeVisible ? 'visible' : 'none'
      );
      
      // IMPROVEMENT 3: Also show/hide route markers
      if (routeMarkersRef.current[routeId]) {
        routeMarkersRef.current[routeId].forEach(marker => {
          if (marker) {
            const el = marker.getElement();
            if (shouldBeVisible) {
              el.style.display = 'flex';
            } else {
              el.style.display = 'none';
            }
          }
        });
      }
    }
  });
  
  // Update visible routes set
  setVisibleRoutes(prev => {
    const newSet = new Set();
    prev.forEach(routeId => {
      if (filteredRouteIds.has(routeId) && showBikeRoutes) {
        newSet.add(routeId);
      }
    });
    return newSet;
  });
  
}, [map, bikeRouteSearchQuery, difficultyFilter, roadTypeFilter, showBikeRoutes]);

// Fit map to show the entire route
const fitMapToRoute = (coordinates) => {
  if (!map || !coordinates || coordinates.length === 0) return;
  
  const bounds = coordinates.reduce((bounds, coord) => {
    return bounds.extend(coord);
  }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
  
  map.fitBounds(bounds, {
    padding: { 
      top: 50, 
      bottom: isMobileView ? 150 : 50, // Add extra padding for mobile bottom nav
      left: isSidebarOpen ? 350 : 50, 
      right: 50 
    }
  });
};

// IMPROVEMENT 2 & 3: Enhanced toggle route visibility with marker control
const toggleRouteVisibility = (routeId) => {
  if (!map) return;
  
  const layerId = `route-layer-${routeId}`;
  
  if (map.getLayer(layerId)) {
    const currentVisibility = map.getLayoutProperty(layerId, 'visibility');
    const newVisibility = currentVisibility === 'visible' ? 'none' : 'visible';
    
    map.setLayoutProperty(layerId, 'visibility', newVisibility);
    
    // IMPROVEMENT 3: Also toggle route markers
    if (routeMarkersRef.current[routeId]) {
      routeMarkersRef.current[routeId].forEach(marker => {
        if (marker) {
          const el = marker.getElement();
          if (newVisibility === 'visible') {
            el.style.display = 'flex';
          } else {
            el.style.display = 'none';
          }
        }
      });
    }
    
    // Update visible routes state
    setVisibleRoutes(prev => {
      const newSet = new Set(prev);
      if (newVisibility === 'visible') {
        newSet.add(routeId);
      } else {
        newSet.delete(routeId);
      }
      return newSet;
    });
  }
};

// IMPROVEMENT 3: Enhanced remove route function with marker cleanup
const removeRoute = (routeId) => {
  if (!map) return;
  
  const layerId = `route-layer-${routeId}`;
  const sourceId = `route-source-${routeId}`;
  
  // Remove layer if it exists
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
    routeLayerIds.current.delete(layerId);
  }
  
  // Remove source if it exists
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
    routeSourceIds.current.delete(sourceId);
  }
  
  // IMPROVEMENT 3: Remove route markers
  if (routeMarkersRef.current[routeId]) {
    routeMarkersRef.current[routeId].forEach(marker => {
      if (marker) marker.remove();
    });
    delete routeMarkersRef.current[routeId];
  }
  
  // Remove from visible routes
  setVisibleRoutes(prev => {
    const newSet = new Set(prev);
    newSet.delete(routeId);
    return newSet;
  });
  
  // Clear selected route if it's the one being removed
  if (selectedBikeRoute && selectedBikeRoute._id === routeId) {
    setSelectedBikeRoute(null);
  }
};

// Show all routes or clear all routes
const toggleAllRoutes = (showAll) => {
  if (showAll) {
    // Show all filtered routes
    const filteredRoutes = filterBikeRoutes();
    filteredRoutes.forEach(route => {
      if (!visibleRoutes.has(route._id)) {
        fetchAndDisplayGpx(route._id);
      }
    });
  } else {
    // Clear all routes
    visibleRoutes.forEach(routeId => {
      removeRoute(routeId);
    });
  }
};

// IMPROVEMENT 2: Mobile-friendly route management component
const MobileRouteControls = () => {
  if (!isMobileView) return null;

  return (
    <div className="bg-white border-t p-3">
      {/*
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900 text-sm">{t('map.routes.display.title', 'Route Visibility')}</h3>
        <button
          onClick={() => setShowBikeRoutes(!showBikeRoutes)}
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            showBikeRoutes 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-500'
          }`}
        >
        {showBikeRoutes
          ? t('map.routes.display.hideAll', 'Hide All Routes')
          : t('map.routes.display.showAll', 'Show All Routes')}
        </button>
      </div>
      */}
      {showBikeRoutes && (
        <div className="space-y-2">
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => toggleAllRoutes(true)}
              className="flex-1 bg-green-100 text-green-700 py-1 px-2 rounded text-xs hover:bg-green-200"
            >
              {t('map.routes.display.loadAll', 'Load All')}
            </button>
            <button
              onClick={() => toggleAllRoutes(false)}
              className="flex-1 bg-red-100 text-red-700 py-1 px-2 rounded text-xs hover:bg-red-200"
            >
              {t('map.routes.display.hideAll', 'Hide All')}
            </button>
          </div>
          
          {/* Quick difficulty filters */}
          <div className="flex gap-1">
            {['easy', 'medium', 'hard'].map(diff => (
              <button
                key={diff}
                onClick={() => setDifficultyFilter(difficultyFilter === diff ? 'all' : diff)}
                className={`flex-1 py-1 px-2 rounded text-xs font-medium ${
                  difficultyFilter === diff
                    ? `bg-${diff === 'easy' ? 'green' : diff === 'medium' ? 'yellow' : 'red'}-100 text-${diff === 'easy' ? 'green' : diff === 'medium' ? 'yellow' : 'red'}-700`
                    : 'bg-gray-100 text-gray-600'
                }`}
                style={{
                  backgroundColor: difficultyFilter === diff ? 
                    (diff === 'easy' ? '#dcfce7' : diff === 'medium' ? '#fef3c7' : '#fee2e2') : 
                    '#f3f4f6',
                  color: difficultyFilter === diff ? 
                    (diff === 'easy' ? '#15803d' : diff === 'medium' ? '#d97706' : '#dc2626') : 
                    '#4b5563'
                }}
              >
                {t(`map.difficulty.${diff}`, diff.charAt(0).toUpperCase() + diff.slice(1))}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Function to add a waypoint marker to the map
const addWaypointMarker = (point, index) => {
  if (!map) return null;
  
  // Get coordinates in the right format
  const coords = getMapboxCoords(point);
  if (!coords) return null;
  
  // Create marker element
  const el = document.createElement('div');
  el.className = 'waypoint-marker';
  el.style.width = '30px';
  el.style.height = '30px';
  el.style.borderRadius = '50%';
  el.style.willChange = 'transform'; // Performance optimization
  el.style.backfaceVisibility = 'hidden'; // Performance optimization
  
  // Color based on position in route
  let color;
  if (index === 0) {
    color = '#22c55e'; // Green for starting point
  } else if (index === waypoints.length - 1) {
    color = '#3b82f6'; // Blue for destination
  } else {
    color = '#f59e0b'; // Amber for waypoints
  }
  
  el.style.backgroundColor = color;
  el.style.border = '3px solid white';
  el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
  el.style.cursor = 'pointer';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.color = 'white';
  el.style.fontWeight = 'bold';
  el.style.fontSize = '12px';
  el.innerText = (index + 1).toString();
  
  // Add marker to map with improved stability settings
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'center',
    pitchAlignment: 'viewport', // Change to viewport for better alignment
    rotationAlignment: 'viewport',
    offset: [0, 0]
  })
  .setLngLat(coords)
  .addTo(map);
  
  // Add popup with waypoint info
  const popup = new mapboxgl.Popup({
    offset: 25,
    closeButton: false
  })
  .setHTML(`
    <div class="popup-content">
      <h3 class="font-bold">${getLocalizedPoiName(point) || t('map.poi.details.customPoint', 'Custom Point')}</h3>
      <p>${index === 0 ? t('map.startingPoint', 'Starting Point') : 
            (index === waypoints.length - 1 ? t('map.poi.details.destination', 'Destination') : 
             t('map.poi.details.waypoint', 'Waypoint') + ` ${index}`)}</p>
    </div>
  `);
  
  marker.setPopup(popup);
  
  // Add to ref
  waypointMarkersRef.current.push(marker);
  
  return marker;
};

// Clear all waypoint markers
const clearWaypointMarkers = () => {
  waypointMarkersRef.current.forEach(marker => {
    if (marker) marker.remove();
  });
  waypointMarkersRef.current = [];
};

// Function to render waypoint markers
const renderWaypointMarkers = () => {
  clearWaypointMarkers();
  
  waypoints.forEach((point, index) => {
    if (point) { // Only add markers for defined waypoints
      addWaypointMarker(point, index);
    }
  });
};

// Update markers when waypoints change
useEffect(() => {
  renderWaypointMarkers();
}, [waypoints, map, language]);  // Added language dependency for marker refresh

// Helper function to create a custom point object from map coordinates
const createCustomPoint = (lngLat, index) => {
  const id = `custom-${Date.now()}-${index}`;
  return {
    _id: id,
    name_en: index === 0 ? t('map.startingPoint', 'Starting Point') : 
            (index === waypoints.length - 1 ? t('map.destination', 'Destination') : 
             t('map.waypoint', 'Waypoint') + ` ${index}`),
    type_en: t('map.customLocation', 'Custom Location'),
    coordinates: {
      lng: lngLat.lng,
      lat: lngLat.lat
    },
    custom: true
  };
};

// Add a custom marker for map clicks
const addCustomMarker = (lngLat) => {
  // Create custom point object
  const customPoint = createCustomPoint(lngLat, currentWaypointIndex);
  
  // Add to waypoints at current index
  const newWaypoints = [...waypoints];
  newWaypoints[currentWaypointIndex] = customPoint;
  setWaypoints(newWaypoints);
  
  // Move to next waypoint selection if needed
  if (currentWaypointIndex === 0) {
    setError(t('map.selectDestination', "Select your destination"));
    setCurrentWaypointIndex(1);
  } else if (currentWaypointIndex === 1 && waypoints.length <= 2) {
    // If we're selecting the destination (second point)
    setRouteSelectionStep(2);
    setError(null);
    fetchMultiPointRoute(newWaypoints, routeMode, routePreference);
  }
  
  return customPoint;
};

// Function to handle POI selection for waypoints
const selectPoiAsWaypoint = (poi) => {
  const newWaypoints = [...waypoints];
  newWaypoints[currentWaypointIndex] = poi;
  setWaypoints(newWaypoints);
  
  // Move to next waypoint selection if needed
  if (currentWaypointIndex === 0) {
    setError(t('map.selectDestination', "Select your destination"));
    setCurrentWaypointIndex(1);
  } else if (currentWaypointIndex === 1 && waypoints.length <= 2) {
    // If we're selecting the destination (second point)
    setRouteSelectionStep(2);
    setError(null);
    fetchMultiPointRoute(newWaypoints, routeMode, routePreference);
  }
};

// Modify the fetchMultiPointRoute function for better waypoint handling
const fetchMultiPointRoute = async (points, mode, preference) => {
  const MAX_WAYPOINTS = 25;
  const MAX_DISTANCES = {
    walking: 100,   // 100 km
    cycling: 6000,   // 300 km
    driving: 3000    // 3000 km
  };

  try {
    // Initial validation
    if (!points || points.length < 2) {
      throw new Error(t('map.errors.minWaypoints', 'At least two points are required'));
    }
    
    if (points.length > MAX_WAYPOINTS) {
      throw new Error(t('map.errors.maxWaypoints', {
        max: MAX_WAYPOINTS,
        defaultValue: `Maximum ${MAX_WAYPOINTS} waypoints allowed`
      }));
    }

    setIsRoutingLoading(true);
    setError(null);
    setRouteAlternatives([]);
    setRouteInfo(null);

    // Validate and process waypoints
    const validatedWaypoints = points.map((point, index) => {
      // Null check
      if (!point) {
        throw new Error(t('map.errors.missingWaypoint', {
          index: index + 1,
          defaultValue: `Waypoint ${index + 1} not set`
        }));
      }

      // Coordinate validation
      const coords = getMapboxCoords(point);
      if (!coords) {
        throw new Error(t('map.errors.invalidCoordinates', {
          name: getLocalizedPoiName(point) || t('map.waypoint', 'Waypoint'),
          defaultValue: `Invalid coordinates for ${getLocalizedPoiName(point) || 'Waypoint'}`
        }));
      }

      // Numeric validation
      if (isNaN(coords[0]) || isNaN(coords[1])) {
        throw new Error(t('map.errors.nonNumericCoordinates', {
          name: getLocalizedPoiName(point) || t('map.waypoint', 'Waypoint'),
          defaultValue: `Non-numeric coordinates for ${getLocalizedPoiName(point) || 'Waypoint'}`
        }));
      }

      // Range validation
      if (Math.abs(coords[0]) > 180) {
        throw new Error(t('map.errors.lngRange', {
          lng: coords[0],
          defaultValue: `Invalid longitude value: ${coords[0]}`
        }));
      }
      if (Math.abs(coords[1]) > 90) {
        throw new Error(t('map.errors.latRange', {
          lat: coords[1],
          defaultValue: `Invalid latitude value: ${coords[1]}`
        }));
      }

      return { point, coords };
    });

    // Distance validation between consecutive points
    for (let i = 0; i < validatedWaypoints.length - 1; i++) {
      const from = validatedWaypoints[i].coords;
      const to = validatedWaypoints[i + 1].coords;
      
      const distance = turf.distance(
        turf.point([from[0], from[1]]),
        turf.point([to[0], to[1]]),
        { units: 'kilometers' }
      );

      if (distance > MAX_DISTANCES[mode]) {
        throw new Error(t('map.errors.distanceExceeded', {
          from: i + 1,
          to: i + 2,
          mode: t(`map.modes.${mode}`, mode),
          max: MAX_DISTANCES[mode],
          distance: distance.toFixed(1),
          defaultValue: `Segment ${i + 1}-${i + 2}: ${distance.toFixed(1)}km exceeds ${MAX_DISTANCES[mode]}km limit for ${mode}`
        }));
      }
    }

    // Build API request
    const coordinatesPath = validatedWaypoints
      .map(wp => `${wp.coords[0]},${wp.coords[1]}`)
      .join(';');

    const baseUrl = `https://api.mapbox.com/directions/v5/mapbox/${mode}/${encodeURIComponent(coordinatesPath)}`;
    const params = new URLSearchParams({
      alternatives: preference === 'shortest' ? 'false' : 'true',
      geometries: 'geojson',
      overview: 'full',
      steps: 'true',
      language: language.split('-')[0],
      access_token: mapboxgl.accessToken,
      waypoints_per_route: 'true',
      approaches: validatedWaypoints.map(() => 'curb').join(';')
    });

    const response = await fetch(`${baseUrl}?${params}`);
    const data = await response.json();

    // Handle API errors
    if (!response.ok || data.code !== 'Ok') {
      const errorMessage = data.message || t('map.errors.directionsFailure', 
        'Failed to calculate route directions');
      throw new Error(`${response.status} - ${errorMessage}`);
    }

    if (!data.routes?.length) {
      throw new Error(t('map.errors.noRouteFound', 'No viable route found'));
    }

    // Process successful response
    const processedRoutes = data.routes.map(route => ({
      ...route,
      originalWaypoints: validatedWaypoints,
      waypoints: data.waypoints || [],
      geometry: route.geometry || { type: 'LineString', coordinates: [] }
    }));

    // Update state
    setRouteAlternatives(processedRoutes);
    setSelectedRouteIndex(0);
    displayRoutePlan(processedRoutes[0], points, mode);
    
    setRouteInfo({
      distance: processedRoutes[0].distance,
      duration: processedRoutes[0].duration,
      mode,
      steps: processedRoutes[0].legs.flatMap(leg => leg.steps),
      legs: processedRoutes[0].legs,
      geometry: processedRoutes[0].geometry,
      waypoints: validatedWaypoints
    });

  } catch (error) {
    console.error('Route Calculation Error:', error);
    setError(`${t('map.failedToGetDirections', 'Failed to get directions')}: ${error.message}`);
    clearRoutePlan();
    setRouteAlternatives([]);
    setRouteInfo(null);
  } finally {
    setIsRoutingLoading(false);
  }
};

// Updated displayRoutePlan to draw connecting segments
const displayRoutePlan = (route, routeWaypoints, mode) => {
  if (!map) return;

  // Clear previous route
  clearRoutePlan();

  // Add the main route source
  map.addSource('route', {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: route.geometry
    }
  });

  // Add the main route layer
  map.addLayer({
    id: 'route',
    type: 'line',
    source: 'route',
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': routeModeColors[mode] || routeModeColors.walking,
      'line-width': 5,
      'line-opacity': 0.75
    }
  });
  
  // Create connector segments from waypoints to route if they don't match exactly
  if (route.originalWaypoints) {
    // For each waypoint, create a connector to the route if needed
    route.originalWaypoints.forEach((waypoint, index) => {
      // Get the actual waypoint used by the route (may be snapped to road)
      let waypointOnRoute;
      
      if (route.waypoints && route.waypoints[index]) {
        // Use the snapped waypoint from the route if available
        waypointOnRoute = [route.waypoints[index].location[0], route.waypoints[index].location[1]];
      } else if (index === 0) {
        // For start point, use the first coordinate of the route
        waypointOnRoute = route.geometry.coordinates[0];
      } else if (index === route.originalWaypoints.length - 1) {
        // For end point, use the last coordinate of the route
        waypointOnRoute = route.geometry.coordinates[route.geometry.coordinates.length - 1];
      }
      
      // Only add connector if the points are different
      if (waypointOnRoute && 
          (Math.abs(waypointOnRoute[0] - waypoint.coords[0]) > 0.0001 || 
           Math.abs(waypointOnRoute[1] - waypoint.coords[1]) > 0.0001)) {
        
        // Create a connector line
        const connectorSourceId = `connector-source-${index}`;
        const connectorLayerId = `connector-layer-${index}`;
        
        map.addSource(connectorSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [waypoint.coords, waypointOnRoute]
            }
          }
        });
        
        map.addLayer({
          id: connectorLayerId,
          type: 'line',
          source: connectorSourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': routeModeColors[mode] || routeModeColors.walking,
            'line-width': 3,
            'line-opacity': 0.6,
            'line-dasharray': [1, 1] // Create a dashed line for connectors
          }
        });
        
        // Store IDs for cleanup
        routeSourceIds.current.add(connectorSourceId);
        routeLayerIds.current.add(connectorLayerId);
      }
    });
  }
  
  // Rest of your existing code for fitting bounds, etc.
  const coordinates = route.geometry.coordinates;
  
  const bounds = coordinates.reduce((bounds, coord) => {
    return bounds.extend(coord);
  }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
  
  map.fitBounds(bounds, {
    padding: { 
      top: 50, 
      bottom: isMobileView ? 150 : 50,
      left: isSidebarOpen ? 350 : 50, 
      right: 50 
    }
  });
  
  // Store route information
  setRouteInfo({
    distance: route.distance,
    duration: route.duration,
    mode: mode,
    steps: route.legs.flatMap(leg => leg.steps),
    legs: route.legs,
    geometry: route.geometry
  });

  // Show button to clear routes
  setShowClearRouteButton(true);
  setButtonVisible(true);
};

// Update clearRoutePlan to clean up connector layers
const clearRoutePlan = () => {
  if (!map) return;
  
  // Remove specific route layers first
  if (map.getLayer('route')) {
    map.removeLayer('route');
  }
  
  // Remove route source
  if (map.getSource('route')) {
    map.removeSource('route');
  }
  
  // Clean up any connector layers and sources
  routeLayerIds.current.forEach(id => {
    if (id.startsWith('connector-layer-') && map.getLayer(id)) {
      map.removeLayer(id);
    }
  });
  
  routeSourceIds.current.forEach(id => {
    if (id.startsWith('connector-source-') && map.getSource(id)) {
      map.removeSource(id);
    }
  });

  // Clean up related UI + logic state
  setRouteAlternatives([]);
  setRouteInfo(null);
  setSelectedRouteIndex(null);
  setWaypoints([]);
  setPoiNavigationMode('none');
  setStartingPoi(null);
  startingPoiRef.current = null;
  setShowClearRouteButton(false);

  // Hide button to clear routes
  setShowClearRouteButton(false);
  setButtonVisible(false);
  clearTimeout(hideTimeoutRef.current);
};

// Export route as GeoJSON
const exportRouteAsGeoJSON = () => {
  if (!routeInfo || !routeInfo.geometry) return;
  
  const geojson = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          name: t('map.route', 'Route'),
          mode: routeMode,
          distance: routeInfo.distance,
          duration: routeInfo.duration
        },
        geometry: routeInfo.geometry
      },
      ...waypoints.map((waypoint, index) => {
        const coords = getMapboxCoords(waypoint);
        return {
          type: "Feature",
          properties: {
            name: getLocalizedPoiName(waypoint) || `${t('map.waypoint', 'Waypoint')} ${index + 1}`,
            index: index,
            type: index === 0 ? "origin" : 
                  index === waypoints.length - 1 ? "destination" : 
                  "waypoint"
          },
          geometry: {
            type: "Point",
            coordinates: coords
          }
        };
      })
    ]
  };
  
  // Convert to JSON string
  const geojsonString = JSON.stringify(geojson, null, 2);
  
  // Create a blob and download
  const blob = new Blob([geojsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `route_${new Date().toISOString().slice(0, 10)}.geojson`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Switch to a different route alternative
const switchRouteAlternative = (index) => {
  if (!routeAlternatives[index]) return;
  
  setSelectedRouteIndex(index);
  const route = routeAlternatives[index];
  
  displayRoutePlan(route, waypoints, routeMode);
};

// Toggle between walking and cycling modes
const toggleRouteMode = () => {
  const newMode = routeMode === 'walking' ? 'cycling' : 'walking';
  setRouteMode(newMode);
  
  // If there's already a selected route, update it
  if (waypoints.length >= 2 && !waypoints.includes(null)) {
    fetchMultiPointRoute(waypoints, newMode, routePreference);
  }
};

// Toggle between shortest and fastest routes
const toggleRoutePreference = () => {
  const newPreference = routePreference === 'shortest' ? 'fastest' : 'shortest';
  setRoutePreference(newPreference);
  
  // If there's already a selected route, update it
  if (waypoints.length >= 2 && !waypoints.includes(null)) {
    fetchMultiPointRoute(waypoints, routeMode, newPreference);
  }
};

// Add a new waypoint to the route
const addWaypoint = () => {
  // Add new waypoint before destination
  const newIndex = Math.max(waypoints.length - 1, 1);
  setCurrentWaypointIndex(newIndex);
  // Insert null placeholder
  const newWaypoints = [...waypoints];
  newWaypoints.splice(newIndex, 0, null);
  setWaypoints(newWaypoints);
  
  setError(t('map.selectNewWaypoint', "Select the new waypoint"));
  setRouteSelectionStep(1);
  setMapClickEnabled(true);
  
  // Ensure the map is visible, especially on mobile
  if (isMobileView) {
    setIsSidebarOpen(false);
    setMapListView('map');
  }
  
  // After adding a waypoint, the map should be focused
  if (map) {
    // Add a small delay to ensure state updates have been applied
    setTimeout(() => {
      map.resize();
    }, 100);
  }
};

// Remove a waypoint from the route
const removeWaypoint = (index) => {
  if (waypoints.length <= 2 || index < 0 || index >= waypoints.length) return;
  
  const newWaypoints = [...waypoints];
  newWaypoints.splice(index, 1);
  setWaypoints(newWaypoints);
  
  // Recalculate route if we still have origin and destination
  if (newWaypoints.length >= 2 && !newWaypoints.includes(null)) {
    fetchMultiPointRoute(newWaypoints, routeMode, routePreference);
  }
};

// Start route planning mode
const startRoutePlanning = () => {
  setIsRoutingPanelOpen(true);
  clearRoutePlan();
  clearWaypointMarkers();
  setRouteSelectionStep(0);
  setWaypoints([null, null]); // Initial setup for origin and destination
  setCurrentWaypointIndex(0);
  setMapClickEnabled(true);
  setError(t('map.selectStartingPoint', "Select your starting point"));
  setActiveSidebarTab('planner');
  if (isMobileView) {
    setIsSidebarOpen(false); // Close sidebar on mobile when planning
  }
};

// Clear route planning
const clearRoutePlanning = () => {
  clearRoutePlan();
  clearWaypointMarkers();
  setRouteInfo(null);
  setWaypoints([]);
  setCurrentWaypointIndex(0);
  setRouteAlternatives([]);
  setSelectedRouteIndex(0);
  setRouteSelectionStep(0);
  setMapClickEnabled(false);
  setError(null);
};

// Handle direct map clicks (not on POI markers or routes)
useEffect(() => {
  if (map && mapClickEnabled) {
    const onClick = (e) => {
      // We only want to handle clicks directly on the map, not on markers
      const isMarkerClick = e.originalEvent.target.closest('.marker') || 
                         e.originalEvent.target.closest('.waypoint-marker');
      
      if (!isMarkerClick) {
        const lngLat = e.lngLat;
        
        // Add to waypoints
        addCustomMarker(lngLat);
      }
    };
    
    map.on('click', onClick);
    
    return () => {
      map.off('click', onClick);
    };
  }
}, [map, mapClickEnabled, currentWaypointIndex, waypoints, routeMode, routePreference]);

// Visual indicator when map click is enabled
useEffect(() => {
  if (map) {
    if (mapClickEnabled) {
      // Change cursor to crosshair when map click is enabled
      map.getCanvas().style.cursor = 'crosshair';
    } else {
      map.getCanvas().style.cursor = '';
    }
  }
}, [map, mapClickEnabled]);

// Format distance (meters to kilometers)
const formatDistance = (meters) => {
  if (!meters && meters !== 0) return t('map.notAvailable', 'N/A');
  return (meters / 1000).toFixed(1) + ' ' + t('map.km', 'km');
};

// Format duration (seconds to human-readable)
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return t('map.notAvailable', 'N/A');
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours} ${t('map.hr', 'hr')} ${minutes} ${t('map.min', 'min')}`;
  } else {
    return `${minutes} ${t('map.min', 'min')}`;
  }
};

// Format elevation (meters)
const formatElevation = (meters) => {
  if (!meters && meters !== 0) return t('map.notAvailable', 'N/A');
  return Math.round(meters) + ' ' + t('map.m', 'm');
};

// Get POI categories for filter
const getPoiCategories = () => {
  const categories = new Set();
  pois.forEach(poi => {
    if (poi.category) {
      categories.add(poi.category);
    }
  });
  return Array.from(categories);
};

// Get road types for filter
const getRoadTypes = () => {
  const types = new Set();
  bikeRoutes.forEach(route => {
    if (route.roadType) {
      types.add(route.roadType);
    }
  });
  return Array.from(types);
};

// Toggle between map view and list view on mobile
const toggleMapListView = () => {
  console.log("Clicked map/list switch");
  // Show POIs in list
  setActiveSidebarTab('pois');
  setMapListView(mapListView === 'map' ? 'list' : 'map');
  // Always show sidebar in list view, hide in map view on mobile
  if (isMobileView) {
    setIsSidebarOpen(mapListView === 'map');
  }
};

// Add this function to get the user's current location
const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error(t('map.geolocationNotSupported', 'Geolocation is not supported by your browser')));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve([position.coords.longitude, position.coords.latitude]);
      },
      (error) => {
        console.error("Error getting location:", error);
        reject(new Error(t('map.locationError', 'Could not get your location')));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

const navigateFromCurrentLocation = async (poi) => {
  try {
    setIsRoutingLoading(true);
    setError(null);
    
    // Close any mobile popup
    if (window.currentMobilePopup) {
      window.currentMobilePopup.remove();
      window.currentMobilePopup = null;
    }
    
    // Request location permissions
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      });
    });

    const userCoords = [position.coords.longitude, position.coords.latitude];
    
    // Create user location marker
    const userLocation = {
      _id: `current-location-${Date.now()}`,
      name_en: t('map.poi.details.starting', 'My Location'),
      coordinates: {
        lng: userCoords[0],
        lat: userCoords[1]
      },
      custom: true
    };

    // Set up route planning
    setActiveSidebarTab('planner');
    setIsRoutingPanelOpen(true);
    clearRoutePlanning();
    setWaypoints([userLocation, poi]);
    setRouteSelectionStep(2);
    
    // Calculate route
    await fetchMultiPointRoute([userLocation, poi], routeMode, routePreference);

    // Mobile handling - show route on map
    if (isMobileView) {
      setMapListView('map');
      setIsSidebarOpen(false);
      
      // Show a temporary success message
      //setError(t('map.routeCalculated', 'Route calculated! Tap the route line for directions.'));
      //setTimeout(() => setError(null), 4000);
    }

  } catch (error) {
    console.error('Location Error:', error);
    setError(t('map.errors.locationPermission', 'Please enable location services to use this feature'));
  } finally {
    setIsRoutingLoading(false);
  }
};

const MapLegend = () => {
  if (!isMobileView) return null;

  return (
    <div className="hidden md-block absolute bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg z-10">
      <h3 className="text-sm font-medium mb-3">{t('map.legend', 'Legend')}</h3>
      
      {/* POI Categories */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-gray-500 mb-2">{t('map.pointsOfInterest', 'Points of Interest')}</h4>
        <div className="space-y-2">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" className="mr-2">
              <path fill="#3b82f6" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            <span className="text-xs">{t('map.poi.categories.business', 'Business')}</span>
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" className="mr-2">
              <path fill="#8B4513" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            <span className="text-xs">{t('map.poi.categories.cultural', 'Cultural')}</span>
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" className="mr-2">
              <path fill="#22c55e" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            <span className="text-xs">{t('map.poi.categories.landscape', 'Landscape')}</span>
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" className="mr-2">
              <path fill="#8B5CF6" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            <span className="text-xs">{t('map.poi.categories.religious', 'Religious')}</span>
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" className="mr-2">
              <path fill="#1E5631" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            <span className="text-xs">{t('map.poi.categories.landscape_religious', 'Landscape Religious')}</span>
          </div>
        </div>
      </div>
      
      {/* Bike Routes */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 mb-2">{t('map.bikeRoutes', 'Bike Routes')}</h4>
        <div className="space-y-2">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-2 bg-[#22c55e]"></div>
            <span className="text-xs">{t('map.difficulty.easy', 'Easy')}</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-2 bg-[#f59e0b]"></div>
            <span className="text-xs">{t('map.difficulty.medium', 'Medium')}</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full mr-2 bg-[#ef4444]"></div>
            <span className="text-xs">{t('map.difficulty.hard', 'Hard')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const [showComingSoonOverlay, setShowComingSoonOverlay] = useState(false);

const handleExperiencesClick = () => {
  // COMING SOON BANNER
  //setShowComingSoonOverlay(true);
  //setTimeout(() => setShowComingSoonOverlay(false), 4500);
  
  setActiveSidebarTab('experiences');
  setMapListView('list');
  setIsSidebarOpen(true);
};

const [expandedRouteId, setExpandedRouteId] = useState(null);

return (


    <div className="h-screen w-full bg-white relative flex flex-col">
      {/* Notification banner for POI-to-POI navigation */}
      {poiNavigationMode === 'from' && startingPoi && (
        <div className="fixed top-16 left-0 right-0 z-40">
          {/* Purple header */}
          <div className="bg-purple-600 text-white py-2 text-center">
            <div className="flex items-center justify-center px-4">
              <Route className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {t('map.startingFrom', 'Starting from')} 
                <strong className="mx-1">
                  {getLocalizedPoiName(startingPoi) || t('map.customLocation', 'Custom location')}
                </strong>
              </span>
              <button 
                className="ml-2 bg-white bg-opacity-20 rounded-full p-1"
                onClick={cancelPoiNavigation}
                aria-label={t('map.cancel', 'Cancel')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Yellow call-to-action */}
          <div className="bg-amber-400 text-gray-900 py-2 text-center shadow-md">
            <div className="flex items-center justify-center px-4">
              <span className="text-sm font-medium">
                🚩 {t('map.selectDestination', 'Select a destination POI to continue')}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {showComingSoonOverlay && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center max-w-xs">
            <img
              src="https://www.citypng.com/public/uploads/preview/png-coming-soon-green-stamp-sign-704081694707562oqleleynn2.png"
              alt="Coming Soon"
              className="h-16 w-auto mx-auto mb-3"
            />
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {t('map.comingSoonTitle', 'Coming Soon')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('map.comingSoonText', "We're working hard to bring this feature to life!")}
            </p>
          </div>
        </div>
      )}
            
      {/* Header */}
      <header className="bg-white shadow-md z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                {brand?.logo ? (
                  <img 
                    src={brand.logo} 
                    alt={brand.name + ' Logo'} 
                    className="h-10 w-auto" 
                  />
                ) : (
                  <img 
                    src="/logo-pointer-transparent.png" 
                    alt={t('map.logoAlt', 'Smart AppKey Logo')}
                    className="h-12 w-auto" 
                  />
                )}

                {brand?.secondaryLogo && (
                  <img 
                    src={brand.secondaryLogo} 
                    alt="Secondary Logo" 
                    className="h-10 w-auto" 
                  />
                )}

                <span className={`font-bold text-lg text-[#22c55e] ${isMobileView ? 'hidden' : 'block'}`}>
                  {t('map.appTitle', 'Smart AppKey')}
                </span>
              </Link>
            </div>
            
            {/* Search bar - hide on mobile */}
            <div className={`relative w-full max-w-md mx-4 ${isMobileView ? 'hidden' : 'block'}`}>
              <input
                type="text"
                placeholder={t('map.search.placeholder', 'Search places and routes...')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setBikeRouteSearchQuery(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#22c55e]"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#6b7280]" />
            </div>
            
            {/* Mobile header buttons */}
            {isMobileView && (
              <div className="flex">
                <button 
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={toggleMapListView}
                >
                  {mapListView === 'map' ? (
                    <List className="h-5 w-5 text-[#6b7280]" />
                  ) : (
                    <MapPin className="h-5 w-5 text-[#22c55e]" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {/* Main content */}
      <div className={`${isMobileView && mapListView === 'list' ? 'hidden' : 'flex-1 flex relative'}`}>
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('map.loading.mapData', 'Loading map data...')}</p>
            </div>
          </div>
        )}
        
        {/* GPX loading indicator */}
        {isGpxLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-20">
            <div className="text-center bg-white p-4 rounded-lg shadow-md">
              <div className="w-10 h-10 border-4 border-[#3b82f6] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('map.loading.routeData', 'Loading route data...')}</p>
            </div>
          </div>
        )}
        
        {/* Routing loading indicator */}
        {isRoutingLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-20">
            <div className="text-center bg-white p-4 rounded-lg shadow-md">
              <div className="w-10 h-10 border-4 border-[#f59e0b] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('map.loading.calculating', 'Calculating route...')}</p>
            </div>
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 z-20 px-4 py-2 rounded-lg shadow-lg">
            <p className="text-gray-700">{error}</p>
          </div>
        )}
        
        {/* Map container */}
        <div ref={mapContainer} className="w-full h-full z-0"></div>

        {showClearRouteButton && (
          <div
            className={`absolute top-4 right-4 z-50 transition-opacity duration-300 ${
              buttonVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <button
              onClick={clearRoutePlan}
              className="flex items-center bg-white text-gray-800 border border-gray-300 rounded-full shadow-lg px-3 py-2 hover:bg-gray-100 transition"
              title={t('map.routes.clearRoute', 'Clear Route')}
            >
              <XCircle className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">{t('map.clearRoute', 'Clear Route')}</span>
            </button>
          </div>
        )}

        {/* POI Detail View - Mobile Only */}
        {isMobileView && (
          <MobilePoiDrawer
            poi={selectedPlace}
            onClose={() => setSelectedPlace(null)}
            onNavigateTo={navigateToPoi}
            onNavigateFrom={navigateFromPoi}
            poiNavigationMode={poiNavigationMode}
            setActiveSidebarTab={setActiveSidebarTab}
            setMapListView={setMapListView}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        )}
       
        {/* Sidebar - Hidden in mobile, will be shown in list view */}
        {isSidebarOpen && !isMobileView && (
          <div className="absolute top-0 left-0 h-full w-full md:w-80 bg-white shadow-lg overflow-hidden flex flex-col">
            {/* Tab Navigation */}
            <div className="flex border-b">
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'pois' ? 'text-green-600 border-b-2 border-green-600' :'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('pois')}
              >
                <div className="flex items-center justify-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {t('map.pointsOfInterest', 'POIs')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'routes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('routes')}
              >
                <div className="flex items-center justify-center">
                  <Route className="h-4 w-4 mr-1" />
                  {t('map.bikeRoutes', 'Bike Routes')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'bikerental' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('bikerental')}
              >
                <div className="flex items-center justify-center">
                  <Bike className="h-4 w-4 mr-1" />
                  {t('map.bikeRental', 'Bikes')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'offers' ? 'text-green-600 border-b-2 border-green-600' :'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('offers')}
              >
                <div className="flex items-center justify-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {t('map.offers', 'Offers')}
                </div>
              </button>
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {/* POIs Tab */}
              {activeSidebarTab === 'pois' && (
                <div>
                  {/* POI List */}
                  <div className="divide-y">
                    {selectedPlace ? (
                      // POI Detail View
                      <div className="p-4">
                        <div className="mb-4">
                          <button 
                            onClick={() => setSelectedPlace(null)}
                            className="text-[#22c55e] flex items-center text-sm"
                          >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            {t('map.backToList', 'Back to list')}
                          </button>
                        </div>
                        <h3 className="font-bold text-xl text-[#1f2937] mb-2">{getLocalizedPoiName(selectedPlace)}</h3>
                        
                        {selectedPlace.photo ? (
                          <div className="bg-gray-200 h-48 rounded-lg mb-4 overflow-hidden">
                            <img 
                              src={selectedPlace.photo} 
                              alt={getLocalizedPoiName(selectedPlace)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"%3E%3Crect width="200" height="150" fill="%23f1f1f1"/%3E%3Ctext x="50%" y="50%" font-family="Arial" font-size="18" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-200 h-48 rounded-lg mb-4 flex items-center justify-center">
                            <span className="text-gray-500">{t('map.noImageAvailable', 'No image available')}</span>
                          </div>
                        )}
                        
                        {getLocalizedPoiDescription(selectedPlace) && (
                          <div className="mb-4">
                            <h4 className="font-medium text-[#1f2937] mb-1">{t('map.description', 'Description')}</h4>
                            <p className="text-sm text-[#6b7280]">{getLocalizedPoiDescription(selectedPlace)}</p>
                          </div>
                        )}

                        <div className="mb-4 space-y-3">
                          {selectedPlace.openingTimes && (
                            <div className="flex items-center text-sm text-[#4b5563]">
                              <Clock className="h-4 w-4 mr-2 text-[#6b7280]" />
                              <span>{selectedPlace.openingTimes}</span>
                            </div>
                          )}

                          {selectedPlace.website && (
                            <div className="flex items-center text-sm text-[#4b5563]">
                              <Globe className="h-4 w-4 mr-2 text-[#6b7280]" />
                              <a
                                href={selectedPlace.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate"
                              >
                                {selectedPlace.website}
                              </a>
                            </div>
                          )}
                          {selectedPlace.instagram && (
                            <div className="flex items-center text-sm text-[#4b5563]">
                              <Instagram className="h-4 w-4 mr-2 text-[#6b7280]" />
                              <a
                                href={selectedPlace.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-pink-600 hover:underline"
                              >
                                @{extractInstagramHandle(selectedPlace.instagram)}
                              </a>
                            </div>
                          )}
                          {selectedPlace.facebook && (
                            <div className="flex items-center text-sm text-[#4b5563]">
                              <Facebook className="h-4 w-4 mr-2 text-[#6b7280]" />
                              <a
                                href={selectedPlace.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 hover:underline truncate"
                              >
                                Facebook
                              </a>
                            </div>
                          )}
                          {selectedPlace.address && (
                            <div className="flex items-center text-sm text-[#4b5563]">
                              <MapPin className="h-4 w-4 mr-2 text-[#6b7280]" />
                              <span>{selectedPlace.address}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col space-y-2 mt-6">
                          <button 
                            className="w-full bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
                            onClick={() =>  navigateToPoi(selectedPlace)}
                          >
                            <Compass className="h-4 w-4 mr-2" />
                            {t('map.planner.navigateToThisPoi', 'Navigate there')}
                          </button>
                          
                          <button 
                            className="w-full bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-600 transition-colors flex items-center justify-center"
                            onClick={() => navigateFromPoi(selectedPlace)}
                          >
                            <Route className="h-4 w-4 mr-2" />
                            {t('map.planner.navigateFromThisPoi', 'Navigate from here')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // POI List View
                      <>
                        {filterPois().length > 0 ? (
                          filterPois().map(poi => (
                            <div 
                              key={poi._id} 
                              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setSelectedPlace(poi);
                                // Center map on POI
                                const coords = getMapboxCoords(poi);
                                if (coords && map) {
                                  map.flyTo({
                                    center: coords,
                                    zoom: 25,
                                    essential: true
                                  });
                                }
                              }}
                            >
                              <div className="flex items-start">
                                <div className="h-16 w-16 rounded-lg flex-shrink-0 overflow-hidden">
                                  {poi.photo ? (
                                    <img 
                                      src={poi.photo} 
                                      alt={getLocalizedPoiName(poi)}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.className = "hidden";
                                        e.target.parentNode.innerHTML = '<div class="h-full w-full bg-gray-200 flex items-center justify-center"><svg class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg></div>';
                                      }}
                                    />
                                  ) : (
                                    <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                                      <MapPin className="h-6 w-6 text-[#22c55e]" />
                                    </div>
                                  )}
                                </div>
                                <div className="ml-3 flex-1">
                                  <h3 className="font-medium text-[#1f2937]">{getLocalizedPoiName(poi)}</h3>
                                  <p className="text-sm text-[#6b7280] capitalize">
                                    {poi.category ? 
                                      t(`map.poi.categories.${poi.category}`, poi.category.replace('_', ' ')) : 
                                      getLocalizedPoiType(poi)}
                                  </p>
                                  <p className="text-xs text-[#6b7280] mt-1 truncate max-w-[200px]">
                                    {getLocalizedPoiDescription(poi) ? 
                                      getLocalizedPoiDescription(poi).substring(0, 60) + 
                                      (getLocalizedPoiDescription(poi).length > 60 ? '...' : '') : 
                                      t('map.noDescriptionAvailable', 'No description available')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-8 text-center text-[#6b7280]">
                            {searchQuery ? (
                              <p>{t('map.noPlacesFound', 'No places found matching')}: "{searchQuery}"</p>
                            ) : (
                              <p>{t('map.noPointsOfInterest', 'No points of interest available')}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Bike Routes Tab */}
              {activeSidebarTab === 'routes' && (
                <div>
                  {/* Routes List */}
                  <div className="divide-y">
                    {selectedBikeRoute ? (
                      // Bike Route Detail View
                      <div className="p-4">
                        <div className="mb-4">
                          <button 
                            onClick={() => setSelectedBikeRoute(null)}
                            className="text-[#3b82f6] flex items-center text-sm"
                          >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            {t('map.routes.details.backToRoutes', 'Back to routes')}
                          </button>
                        </div>
                        
                        <h3 className="font-bold text-xl text-[#1f2937] mb-2">
                          {selectedBikeRoute.name?.[i18n.language] || selectedBikeRoute.name?.en || ''}
                        </h3>
                        
                        <div className="flex items-center mb-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                            selectedBikeRoute.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            selectedBikeRoute.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {t(`map.difficulty.${selectedBikeRoute.difficulty}`, 
                               selectedBikeRoute.difficulty.charAt(0).toUpperCase() + selectedBikeRoute.difficulty.slice(1))}
                          </span>
                          <span className="mx-2">•</span>
                          <span className="text-sm text-gray-600 capitalize">
                            {t(`map.roadTypes.${selectedBikeRoute.roadType}`, selectedBikeRoute.roadType || 'Mixed')}
                          </span>
                        </div>
                        
                        {selectedBikeRoute.description?.[i18n.language] || selectedBikeRoute.description?.en ? (
                          <p className="text-sm text-gray-600 mb-4">
                            {selectedBikeRoute.description[i18n.language] || selectedBikeRoute.description.en}
                          </p>
                        ) : null}

                        
                        {/* Stats grid */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50 p-3 rounded-md">
                            <div className="text-gray-500 text-xs mb-1">{t('map.distance', 'Distance')}</div>
                            <div className="font-semibold text-gray-900">
                              {formatDistance(selectedBikeRoute.stats?.totalDistance * 1000 || 0)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <div className="text-gray-500 text-xs mb-1">{t('map.elevationGain', 'Elevation Gain')}</div>
                            <div className="font-semibold text-gray-900">
                              {formatElevation(selectedBikeRoute.stats?.elevationGain || 0)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <div className="text-gray-500 text-xs mb-1">{t('map.maxElevation', 'Max Elevation')}</div>
                            <div className="font-semibold text-gray-900">
                              {formatElevation(selectedBikeRoute.stats?.maxElevation || 0)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <div className="text-gray-500 text-xs mb-1">{t('map.minElevation', 'Min Elevation')}</div>
                            <div className="font-semibold text-gray-900">
                              {formatElevation(selectedBikeRoute.stats?.minElevation || 0)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action buttons - IMPROVEMENT 4: Removed download GPX button */}
                        <div className="flex space-x-2 mt-4">
                          <button 
                            className="flex-1 py-2 px-3 rounded-md flex items-center justify-center bg-green-100 text-green-700 hover:bg-green-200"
                            onClick={() => {
                              // Get the first and last points from the path
                              if (selectedBikeRoute.path && selectedBikeRoute.path.length >= 2) {
                                const startPoint = selectedBikeRoute.path[0];
                                const endPoint = selectedBikeRoute.path[selectedBikeRoute.path.length - 1];
                                
                                // Create POI-like objects
                                const startPoi = {
                                  _id: `start-${selectedBikeRoute._id}`,
                                  name_en: t('map.startOf', 'Start of') + ` ${selectedBikeRoute.name.en}`,
                                  type_en: t('map.startingPoint', 'Starting Point'),
                                  coordinates: {
                                    lng: startPoint[0],
                                    lat: startPoint[1]
                                  },
                                  custom: true
                                };
                                
                                const endPoi = {
                                  _id: `end-${selectedBikeRoute._id}`,
                                  name_en: t('map.endOf', 'End of') + ` ${selectedBikeRoute.name.en}`,
                                  type_en: t('map.endPoint', 'End Point'),
                                  coordinates: {
                                    lng: endPoint[0],
                                    lat: endPoint[1]
                                  },
                                  custom: true
                                };
                                
                                // Switch to planner tab and set up route
                                setActiveSidebarTab('planner');
                                setIsRoutingPanelOpen(true);
                                clearRoutePlanning();
                                setWaypoints([startPoi, endPoi]);
                                setRouteSelectionStep(2);
                                setRouteMode('cycling');
                                fetchMultiPointRoute([startPoi, endPoi], 'cycling', routePreference);
                                
                                // On mobile, close sidebar and show map
                                if (isMobileView) {
                                  setIsSidebarOpen(false);
                                  setMapListView('map');
                                }
                              }
                            }}
                          >
                            <Route className="h-4 w-4 mr-2" />
                            {t('map.routes.details.navigate', 'Navigate This Route')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Bike Routes List View
                      <>
                        {filterBikeRoutes().length > 0 ? (
                          filterBikeRoutes().map(route => (
                            <div 
                              key={route._id} 
                              className={`p-4 hover:bg-gray-50 transition-colors ${
                                visibleRoutes.has(route._id) ? 'bg-blue-50/30' : ''
                              }`}
                            >
                              <div className="flex items-start">
                                <div className="h-10 w-10 rounded-full flex-shrink-0 bg-opacity-10 flex items-center justify-center mr-2"
                                     style={{ backgroundColor: `${routeColors[route.difficulty] || routeColors.default}20` }}>
                                  <Bike className="h-5 w-5" style={{ color: routeColors[route.difficulty] || routeColors.default }} />
                                </div>
                                <div 
                                  className="flex-1 min-w-0 cursor-pointer"
                                  onClick={() => {
                                    setSelectedBikeRoute(route);
                                    
                                    // If route is not already visible, display it
                                    if (!visibleRoutes.has(route._id)) {
                                      fetchAndDisplayGpx(route._id);
                                    }
                                    
                                    // If route has path data, fit map to it
                                    if (route.path && route.path.length > 0) {
                                      fitMapToRoute(route.path);
                                    }
                                  }}
                                >
                                  <h3 className="font-medium text-[#1f2937]">
                                    {route.name?.[i18n.language] || route.name?.en || ''}
                                  </h3>

                                  <div className="flex items-center text-sm text-[#6b7280] mt-1">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      route.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                      route.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {t(`map.difficulty.${route.difficulty}`, 
                                         route.difficulty.charAt(0).toUpperCase() + route.difficulty.slice(1))}
                                    </span>
                                    <span className="mx-2">•</span>
                                    <span>{t(`map.roadTypes.${route.roadType}`, route.roadType || 'Mixed')}</span>
                                    <span className="mx-2">•</span>
                                    <span>
                                      {route.stats?.totalDistance ? 
                                        formatDistance(route.stats.totalDistance * 1000) : 
                                        t('map.distanceNA', 'Distance N/A')}
                                    </span>
                                  </div>
                                    {route.description && (
                                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                        {route.description[i18n.language] || route.description.en || ''}
                                      </p>
                                    )}
                                </div>
                                <div className="flex-shrink-0 flex space-x-1">
                                  <button
                                    onClick={() => toggleRouteVisibility(route._id)}
                                    className={`p-1 rounded-full ${visibleRoutes.has(route._id) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
                                    title={visibleRoutes.has(route._id) ? t('map.filter.hide', "Hide route") : t('map.filter.show', "Show route")}
                                  >
                                    {visibleRoutes.has(route._id) ? 
                                      <Eye className="h-4 w-4" /> : 
                                      <EyeOff className="h-4 w-4" />}
                                  </button>
                                  <button
                                    onClick={() => fetchAndDisplayGpx(route._id)}
                                    className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                                    title={t('map.loadThisRoute', 'Load this route')}
                                  >
                                    <Bike className="h-4 w-4 text-gray-700" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-8 text-center text-[#6b7280]">
                            {bikeRouteSearchQuery || difficultyFilter !== 'all' || roadTypeFilter !== 'all' ? (
                              <p>{t('map.noRoutesMatchFilters', 'No routes match your filters')}</p>
                            ) : (
                              <p>{t('map.noBikeRoutesAvailable', 'No bike routes available')}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Offers/Experiences/Bikes search */}
              {activeSidebarTab === 'offers' && (
                <div className="p-4">
                  <OffersList searchQuery={offerSearchQuery} />
                </div>
              )}

              {activeSidebarTab === 'experiences' && (
                <div className="p-4">
                  <ExperiencesList searchQuery={experienceSearchQuery} />
                </div>
              )}

              {activeSidebarTab === 'bikerental' && (
                <div className="p-4">
                  <BikeRentalList 
                    searchQuery={searchQuery} 
                    setActiveSidebarTab={setActiveSidebarTab}
                    setMapListView={setMapListView}
                    setIsSidebarOpen={setIsSidebarOpen}
                  />
                </div>
              )}  
            </div>
            
            {/* Close button */}
            <button 
              onClick={() => {
                setIsSidebarOpen(false);
                if (isMobileView) {
                  setMapListView('map');
                }
              }}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-[#6b7280]" />
            </button>
          </div>
        )}
        
        {/* Map Legend */}
        <MapLegend />
      </div>
      
      {/* Mobile list view - shows only when in list mode */}
      {isMobileView && mapListView === 'list' && (
        <div className="flex-1 overflow-auto bg-white">
          {/* Only show search + tabs if NOT on profile */}
          {activeSidebarTab !== 'profile' && (
            <>
            {/* Search bar for mobile list */}
            <div className="p-4 border-b">
              <div className="relative">
                <input
                  type="text"
                  placeholder={
                    activeSidebarTab === 'pois'
                      ? t('map.search.placesPlaceholder', 'Search places...')
                      : activeSidebarTab === 'routes'
                      ? t('map.search.routesPlaceholder', 'Search routes...')
                      : activeSidebarTab === 'offers'
                      ? t('map.search.offersPlaceholder', 'Search offers...')
                      : activeSidebarTab === 'experiences'
                      ? t('map.search.experiencesPlaceholder', 'Search experiences...')
                      : t('map.search.bikesPlaceholder', 'Search bikes...')
                  }
                  value={
                    activeSidebarTab === 'pois'
                      ? searchQuery
                      : activeSidebarTab === 'routes'
                      ? bikeRouteSearchQuery
                      : activeSidebarTab === 'offers'
                      ? offerSearchQuery
                      : activeSidebarTab === 'experiences'
                      ? experienceSearchQuery
                      : searchQuery
                  }
                  onChange={(e) => {
                    const value = e.target.value;

                    if (activeSidebarTab === 'routes') {
                      setBikeRouteSearchQuery(value);
                    } else if (activeSidebarTab === 'offers') {
                      setOfferSearchQuery(value);
                    } else if (activeSidebarTab === 'experiences') {
                      setExperienceSearchQuery(value);
                    } else {
                      setSearchQuery(value);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-3 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#22c55e]"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-[#6b7280]" />
              </div>
            </div>
            
            {/* Tabs for mobile list view */}
            {/* HIDDEN NOW */}
            <div className="hidden">
            <div className="flex border-b">
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'pois' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('pois')}
              >
                <div className="flex items-center justify-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {t('map.pointsOfInterest', 'POIs')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'routes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('routes')}
              >
                <div className="flex items-center justify-center">
                  <Bike className="h-4 w-4 mr-1" />
                  {t('map.bikeRoutes', 'Routes')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'bikerental' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('bikerental')}
              >
                <div className="flex items-center justify-center">
                  <Bike className="h-4 w-4 mr-1" />
                  {t('map.bikeRental', 'Bikes')}
                </div>
              </button>
              <button 
                className={`flex-1 py-3 font-medium text-sm ${activeSidebarTab === 'offers' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveSidebarTab('offers')}
              >
                <div className="flex items-center justify-center">
                  <BadgePercent className="h-4 w-4 mr-1" />
                  {t('map.offers', 'Offers')}
                </div>
              </button>
            </div>
            </div>
            </>
          )}
          
          {/* IMPROVEMENT 2: Add mobile route controls for routes tab */}
          {activeSidebarTab === 'routes' && <MobileRouteControls />}
          
          {/* Content based on active tab */}
          {activeSidebarTab === 'pois' && (
            <div className="divide-y">
              {/* POI List */}
              {filterPois().map(poi => (
                <div 
                  key={poi._id} 
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedPlace(poi);
                    // Center map on POI
                    const coords = getMapboxCoords(poi);
                    if (coords && map) {
                      map.flyTo({
                        center: coords,
                        zoom: 25,
                        essential: true
                      });
                    }
                    
                    // Switch to map view
                    setMapListView('map');
                  }}
                >
                  <div className="flex items-start">
                    <div className="h-16 w-16 rounded-lg flex-shrink-0 overflow-hidden">
                      {poi.photo ? (
                        <img 
                          src={poi.photo} 
                          alt={getLocalizedPoiName(poi)}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.className = "hidden";
                            e.target.parentNode.innerHTML = '<div class="h-full w-full bg-gray-200 flex items-center justify-center"><svg class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg></div>';
                          }}
                        />
                      ) : (
                        <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-[#22c55e]" />
                        </div>
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="font-medium text-[#1f2937]">{getLocalizedPoiName(poi)}</h3>
                      <p className="text-sm text-[#6b7280] capitalize">
                        {poi.category ? 
                          t(`map.poi.categories.${poi.category}`, poi.category.replace('_', ' ')) : 
                          getLocalizedPoiType(poi)}
                      </p>
                      <p className="text-xs text-[#6b7280] mt-1 truncate max-w-[200px]">
                        {getLocalizedPoiDescription(poi) ? 
                          getLocalizedPoiDescription(poi).substring(0, 60) + 
                          (getLocalizedPoiDescription(poi).length > 60 ? '...' : '') : 
                          t('map.noDescriptionAvailable', 'No description available')}
                      </p>
                      
                      {/* POI action buttons */}
                      <div className="flex space-x-2 mt-2">
                        <button 
                          className="text-xs py-1 px-2 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToPoi(poi);
                            if (poiNavigationMode === 'from') {
                              setMapListView('map');
                            }
                          }}
                        >
                          {t('map.planner.navigateToThisPoi', 'Navigate To')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {filterPois().length === 0 && (
                <div className="p-8 text-center text-[#6b7280]">
                  {searchQuery ? (
                    <p>{t('map.poi.noResults', 'No places found matching')}: "{searchQuery}"</p>
                  ) : (
                    <p>{t('map.poi.noPlaces', 'No points of interest available')}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {activeSidebarTab === 'routes' && (
            <div className="divide-y">
              {/* Routes List */}
              {filterBikeRoutes().map(route => {
                const isExpanded = expandedRouteId === route._id;

                return (
                  <div 
                    key={route._id} 
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      visibleRoutes.has(route._id) ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className="flex items-start">
                      {/* Icon */}
                      <div className="h-10 w-10 rounded-full flex-shrink-0 bg-opacity-10 flex items-center justify-center mr-3"
                          style={{ backgroundColor: `${routeColors[route.difficulty] || routeColors.default}20` }}>
                        <Bike className="h-5 w-5" style={{ color: routeColors[route.difficulty] || routeColors.default }} />
                      </div>

                      {/* Name + Summary */}
                      <div className="flex-1 min-w-0">
                        <div 
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedBikeRoute(route);
                            if (!visibleRoutes.has(route._id)) fetchAndDisplayGpx(route._id);
                            if (route.path?.length) fitMapToRoute(route.path);
                            setMapListView('map');
                          }}
                        >
                          <h3 className="font-medium text-[#1f2937]">
                            {route.name?.[i18n.language] || route.name?.en || ''}
                          </h3>

                          <div className="flex items-center text-sm text-[#6b7280] mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              route.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                              route.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {t(`map.difficulty.${route.difficulty}`, route.difficulty)}
                            </span>
                            <span className="mx-2">•</span>
                            <span>{formatDistance(route.stats?.totalDistance * 1000 || 0)}</span>
                          </div>
                        </div>

                        {/* Expandable Stats Toggle */}
                        <div
                          onClick={() => setExpandedRouteId(isExpanded ? null : route._id)}
                          className="mt-2 flex items-center space-x-1 cursor-pointer text-sm text-gray-500 hover:text-gray-700 select-none"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <span>
                            {t('map.routes.toggleStats', 'Details')}
                          </span>
                        </div>

                        {/* Expanded Section */}
                        {isExpanded && (
                          <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-md space-y-4">
                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="text-xs text-gray-500">{t('map.elevationGain', 'Elevation Gain')}</div>
                                <div className="font-medium">{formatElevation(route.stats?.elevationGain || 0)}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">{t('map.maxElevation', 'Max Elevation')}</div>
                                <div className="font-medium">{formatElevation(route.stats?.maxElevation || 0)}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">{t('map.minElevation', 'Min Elevation')}</div>
                                <div className="font-medium">{formatElevation(route.stats?.minElevation || 0)}</div>
                              </div>
                            </div>

                            {/* Navigate Button */}
                            {route.outdooractiveUrl && (
                              <a
                                href={route.outdooractiveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
                              >
                                🚴‍♂️ {t('map.routes.details.navigate', 'Navigate')}
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Visibility Toggle */}
                      <div className="flex-shrink-0 flex flex-col space-y-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRouteVisibility(route._id);
                          }}
                          className={`p-2 rounded-full ${visibleRoutes.has(route._id) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
                          title={visibleRoutes.has(route._id) ? t('map.hideRoute', "Hide route") : t('map.showRoute', "Show route")}
                        >
                          {visibleRoutes.has(route._id) ? 
                            <Eye className="h-4 w-4" /> : 
                            <EyeOff className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              
              {filterBikeRoutes().length === 0 && (
                <div className="p-8 text-center text-[#6b7280]">
                  {bikeRouteSearchQuery ? (
                    <p>{t('map.routes.noResults', 'No routes found matching')}: "{bikeRouteSearchQuery}"</p>
                  ) : (
                    <p>{t('map.routes.noResults', 'No bike routes available')}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {activeSidebarTab === 'offers' && (
            <div className="p-4">
              <OffersList 
                searchQuery={offerSearchQuery} 
                setActiveSidebarTab={setActiveSidebarTab}
                setMapListView={setMapListView}
                setIsSidebarOpen={setIsSidebarOpen}
                setSelectedPlace={setSelectedPlace}
                map={map}
                getMapboxCoords={getMapboxCoords}
              />
            </div>
          )}

          {activeSidebarTab === 'experiences' && (
            <div className="p-4">
              <ExperiencesList 
                searchQuery={experienceSearchQuery} 
                setActiveSidebarTab={setActiveSidebarTab}
                setMapListView={setMapListView}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </div>
          )}

          {activeSidebarTab === 'bikerental' && (
            <div className="p-4">
              <BikeRentalList 
                searchQuery={searchQuery} 
                setActiveSidebarTab={setActiveSidebarTab}
                setMapListView={setMapListView}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </div>
          )}
          
          {activeSidebarTab === 'profile' && (
            <div className="p-4">
              <UserProfile  showCloseButton={true} />
            </div>
          )}
        </div>
      )}
      
      {/* Mobile Bottom Navigation Bar */}
      {isMobileView && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex justify-center">
          <div className="h-16 bg-white shadow-lg border-t flex items-center justify-around w-full max-w-md mx-auto">
            
            {/* ICON 1 - ROUTES */}
            <button
              className={`flex flex-col items-center justify-center w-1/4 p-1 ${
                activeSidebarTab === 'routes' ? 'text-blue-600' : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveSidebarTab('routes');
                setMapListView('list');
                setIsSidebarOpen(true);
              }}
            >
              <Route className="h-5 w-5" />
              <span className="text-xs mt-1">{t('map.mobileNav.routes', 'Routes')}</span>
            </button>
            
            {/* ICON 2 - OFFERS */}
            <button
              className={`flex flex-col items-center justify-center w-1/4 p-1 ${
                activeSidebarTab === 'offers' ? 'text-blue-600' : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveSidebarTab('offers');
                setMapListView('list');
                setIsSidebarOpen(true);
              }}
            >
              <Tag className="h-5 w-5 " />
              <span className="text-xs mt-1">{t('map.mobileNav.offers', 'Offers')}</span>
            </button>

            {/* ICON 3 - BIKE RENTAL */}
            <button
              className={`flex flex-col items-center justify-center w-1/4 p-1 ${
                activeSidebarTab === 'bikerental' ? 'text-blue-600' : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveSidebarTab('bikerental');
                setMapListView('list');
                setIsSidebarOpen(true);
              }}
            >
              <Bike className="h-5 w-5 " />
              <span className="text-xs mt-1">{t('map.mobileNav.bikes', 'Bikes')}</span>
            </button>
            
            {/* ICON 4 - EXPERIENCES */}
            <button
              className={`flex flex-col items-center justify-center w-1/4 p-1 ${
                activeSidebarTab === 'experiences' ? 'text-blue-600' : 'text-gray-500'
              }`}
              onClick={handleExperiencesClick}
            >
              <Star className="h-5 w-5 " />
              <span className="text-xs mt-1">{t('map.mobileNav.experiences', 'Experiences')}</span>
            </button>
            
            {/* ICON 5 - PROFILE */}
            <button
              className={`flex flex-col items-center justify-center w-1/4 p-1 ${
                activeSidebarTab === 'profile' ? 'text-blue-600' : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveSidebarTab('profile');
                setMapListView('list');
                setIsSidebarOpen(true);
              }}
            >
              <User className="w-5 h-5 " />
              <span className="text-xs mt-1">
                {user ? t('map.mobileNav.profile', 'Profile') : t('map.mobileNav.login', 'Login')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegratedMap;